import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { useT } from '@/i18n';
import { ErrorBox, Field } from '@/components/ui';
import { api } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import { adoptSession } from '@/lib/prefs';
import { logError } from '@/lib/log';
import type { AuthSession } from '@/lib/api/types';
import { AuthLayout } from './AuthLayout';

/** Registration. With `inviteToken` it joins that workspace instead of creating one. */
export function RegisterForm({ inviteToken, email: fixedEmail, onDone }: { inviteToken?: string; email?: string; onDone: (s: AuthSession) => void }) {
  const { t, locale } = useT();
  const [name, setName] = useState('');
  const [email, setEmail] = useState(fixedEmail ?? '');
  const [password, setPassword] = useState('');
  const [workspaceName, setWorkspaceName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const fieldError = (path: string) => (error instanceof ApiError ? (error.fieldErrors.find((f) => f.path === path)?.message ?? null) : null);
  const tooShort = password.length > 0 && password.length < 10;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (tooShort) return;
    setBusy(true);
    setError(null);
    try {
      const s = await api.auth.register({ name: name.trim(), email: email.trim(), password, locale, ...(inviteToken ? { inviteToken } : { workspaceName: workspaceName.trim() }) });
      adoptSession(s);
      onDone(s);
    } catch (err) {
      logError('auth', `register failed via=${inviteToken ? 'invite' : 'signup'}`, err);
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="stack" style={{ gap: 14 }}>
      {error && !(error instanceof ApiError && error.fieldErrors.length) ? <ErrorBox error={error} /> : null}
      <Field label={t('yourName')} error={fieldError('name')}>{(p) => <input {...p} className="input" autoComplete="name" required value={name} onChange={(e) => setName(e.target.value)} />}</Field>
      <Field label={t('email')} error={fieldError('email')}>{(p) => <input {...p} className="input" type="email" dir="ltr" autoComplete="email" required readOnly={!!fixedEmail} value={email} onChange={(e) => setEmail(e.target.value)} />}</Field>
      <Field label={t('password')} hint={t('passwordHint')} error={tooShort ? t('passwordHint') : fieldError('password')}>
        {(p) => <input {...p} className="input" type="password" dir="ltr" autoComplete="new-password" required minLength={10} value={password} onChange={(e) => setPassword(e.target.value)} />}
      </Field>
      {!inviteToken ? (
        <Field label={t('workspaceName')} error={fieldError('workspaceName')}>{(p) => <input {...p} className="input" required value={workspaceName} onChange={(e) => setWorkspaceName(e.target.value)} />}</Field>
      ) : null}
      <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? t('creating') : inviteToken ? t('inviteNewAccount') : t('registerBtn')}</button>
    </form>
  );
}

export function RegisterPage() {
  const { t } = useT();
  const navigate = useNavigate();
  return (
    <AuthLayout title={t('registerTitle')} sub={t('registerSub')}>
      <RegisterForm onDone={() => navigate('/', { replace: true })} />
      <p className="muted small" style={{ margin: 0 }}>
        {t('haveAccount')} <Link to="/login">{t('loginBtn')}</Link>
      </p>
    </AuthLayout>
  );
}
